/* Custom element: <social-videos-carousel>
   Requires: Flickity (theme bundle) — resolved from window.theme.Flickity */

(() => {
  'use strict';

  // Ring-distance falloff: inactive frames dim as they move away from center.
  // Active frame's transform/opacity/height live in .social-videos__frame--active.
  const INACTIVE_SCALE = 'scale(0.84)';
  const INACTIVE_HEIGHT = '78%';
  const OPACITY_STEPS = [1, 0.62, 0.54, 0.46, 0.38, 0.3];
  const MIN_OPACITY = 0.3;

  // Flickity loads deferred from the theme bundle; poll briefly for it.
  const INIT_RETRY_DELAY = 150;
  const INIT_MAX_ATTEMPTS = 20;

  // Playback waits until this much of the carousel is on screen. The videos
  // are `preload="none"`, so nothing is fetched before the first play() —
  // gating that call keeps an off-screen carousel at zero video bytes.
  const VISIBILITY_THRESHOLD = 0.25;

  // Crossings to watch: leaving entirely, the threshold, and a midpoint for
  // carousels too tall to ever reach it.
  const VISIBILITY_STEPS = [0, VISIBILITY_THRESHOLD / 2, VISIBILITY_THRESHOLD];

  const SELECTORS = {
    cell: '[data-social-videos-cell]',
    frame: '[data-social-videos-frame]',
    videoWrapper: '[data-social-videos-video]',
    mute: '[data-social-videos-mute]',
    author: '.social-videos__author',
  };

  const CLASSES = {
    frameActive: 'social-videos__frame--active',
    paused: 'is-paused',
    playing: 'is-playing',
  };

  function getFlickity() {
    return window.theme?.Flickity || null;
  }

  class SocialVideosCarousel extends HTMLElement {
    #listeners = null;
    #initTimer = null;
    #initAttempts = 0;
    #cells = [];
    #flkty = null;
    #visibilityObserver = null;
    #isVisible = false;
    #soundRequested = false; // the visitor's last explicit sound choice

    connectedCallback() {
      this.#cells = Array.from(this.querySelectorAll(SELECTORS.cell));
      if (this.#cells.length < 2) return;

      this.#listeners = new AbortController();
      this.#initAttempts = 0;
      this.#init();
    }

    disconnectedCallback() {
      window.clearTimeout(this.#initTimer);
      this.#initTimer = null;

      this.#visibilityObserver?.disconnect();
      this.#visibilityObserver = null;
      this.#isVisible = false;
      this.#soundRequested = false;

      this.#listeners?.abort();
      this.#listeners = null;

      this.#flkty?.destroy();
      this.#flkty = null;
    }

    #init() {
      const Flickity = getFlickity();

      if (!Flickity) {
        if (this.#initAttempts++ < INIT_MAX_ATTEMPTS) {
          this.#initTimer = window.setTimeout(() => this.#init(), INIT_RETRY_DELAY);
        } else {
          console.warn('[social-videos] Flickity not found on window.theme.Flickity.');
        }
        return;
      }

      const { signal } = this.#listeners;

      this.#flkty = new Flickity(this, {
        wrapAround: true,
        pageDots: true,
        prevNextButtons: false,
        cellAlign: 'center',
        cellSelector: SELECTORS.cell,
        selectedAttraction: 0.028,
        friction: 0.32,
        accessibility: true,
        imagesLoaded: true,
      });

      this.#flkty.on('select', () => this.#onSelect());
      this.#flkty.on('staticClick', (event, pointer, cellElement, cellIndex) =>
        this.#onStaticClick(event, cellElement, cellIndex)
      );

      // Recompute once videos/posters have decoded — Flickity's first measure
      // can happen before the parent container has finished laying out.
      if (document.readyState === 'complete') {
        this.#flkty.resize();
      } else {
        window.addEventListener('load', () => this.#flkty?.resize(), { once: true, signal });
      }

      for (const cell of this.#cells) {
        const muteButton = cell.querySelector(SELECTORS.mute);
        if (!muteButton) continue;

        muteButton.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            if (cell !== this.#cells[this.#flkty.selectedIndex]) return;
            this.#toggleMute(cell);
          },
          { signal }
        );
      }

      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

      // Lay the frames out straight away; the observer owns playback.
      this.#applyFalloff();
      this.#observeVisibility();
    }

    /**
     * Start playback when the carousel scrolls into view and stop it again
     * when it leaves, so a carousel far below the fold costs nothing until
     * the visitor actually reaches it.
     */
    #observeVisibility() {
      this.#visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const visible = this.#isSufficientlyVisible(entry);
            if (visible === this.#isVisible) continue;

            this.#isVisible = visible;

            if (visible) {
              this.#playSelected();
            } else {
              this.#pauseAll();
            }
          }
        },
        { threshold: VISIBILITY_STEPS }
      );

      this.#visibilityObserver.observe(this);
    }

    /**
     * `entry.isIntersecting` is true at any ratio above zero, so the observer's
     * threshold never enforces itself — compare the ratio.
     *
     * @param {IntersectionObserverEntry} entry - the carousel's latest entry.
     * @returns {boolean} whether enough of the carousel is on screen to play.
     */
    #isSufficientlyVisible(entry) {
      if (!entry.isIntersecting) return false;
      if (entry.intersectionRatio >= VISIBILITY_THRESHOLD) return true;

      // A carousel taller than the viewport tops out below the threshold.
      const viewportHeight = entry.rootBounds?.height || window.innerHeight;
      return entry.intersectionRect.height >= viewportHeight * VISIBILITY_THRESHOLD;
    }

    /* ---- carousel events ---- */

    #onSelect() {
      this.#applyFalloff();
      this.#playSelected();
    }

    /**
     * A tap on the selected cell toggles sound; a tap on any other cell
     * brings that cell to the center.
     *
     * @param {Event} event - Flickity's originating pointer event.
     * @param {HTMLElement} cellElement - the cell that was tapped.
     * @param {number} [cellIndex] - its index, undefined when the tap
     *   landed outside a cell.
     */
    #onStaticClick(event, cellElement, cellIndex) {
      if (cellIndex === undefined) return;

      if (cellIndex !== this.#flkty.selectedIndex) {
        this.#flkty.select(cellIndex);
        return;
      }

      if (event.target.closest(SELECTORS.mute)) return;
      if (event.target.closest(SELECTORS.author)) return; // let links navigate
      this.#toggleMute(cellElement);
    }

    #onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const index = this.#cells.indexOf(event.target);
      if (index === -1 || !this.#flkty) return;
      this.#flkty.select(index);
    };

    /* ---- falloff ---- */

    /**
     * Shortest distance between two cells around the wrapped carousel.
     *
     * @param {number} index - the cell to measure.
     * @param {number} selected - the centered cell.
     * @returns {number} steps from the center, in either direction.
     */
    #ringDistance(index, selected) {
      const count = this.#cells.length;
      const raw = Math.abs(index - selected);
      return Math.min(raw, count - raw);
    }

    /** Scale and dim each frame by how far it sits from the center. */
    #applyFalloff() {
      const selected = this.#flkty.selectedIndex;

      for (const [index, cell] of this.#cells.entries()) {
        const frame = cell.querySelector(SELECTORS.frame);
        if (!frame) continue;

        const distance = this.#ringDistance(index, selected);

        if (distance === 0) {
          frame.classList.add(CLASSES.frameActive);
          frame.style.transform = '';
          frame.style.opacity = '';
          frame.style.height = '';
          continue;
        }

        frame.classList.remove(CLASSES.frameActive);
        frame.style.transform = INACTIVE_SCALE;
        frame.style.opacity = OPACITY_STEPS[Math.min(distance, OPACITY_STEPS.length - 1)] || MIN_OPACITY;
        frame.style.height = INACTIVE_HEIGHT;
      }
    }

    /* ---- playback ---- */

    /**
     * @param {HTMLElement} cell - a carousel cell.
     * @returns {HTMLVideoElement|null} the cell's video, if it has one.
     */
    #videoOf(cell) {
      return cell.querySelector(SELECTORS.videoWrapper)?.querySelector('video') || null;
    }

    /**
     * Sync a cell's mute button to the video's state.
     *
     * @param {HTMLElement} cell - the cell owning the button.
     * @param {boolean} muted - the video's current muted state.
     */
    #setMuteUI(cell, muted) {
      const button = cell.querySelector(SELECTORS.mute);
      if (!button) return;
      button.dataset.muted = muted ? 'true' : 'false';
      const label = muted ? button.dataset.labelUnmute : button.dataset.labelMute;
      if (label) button.setAttribute('aria-label', label);
    }

    /**
     * Pause a cell's video, re-mute it and clear its playback state.
     *
     * @param {HTMLElement} cell - the cell to stop.
     */
    #pauseCell(cell) {
      const video = this.#videoOf(cell);
      const frame = cell.querySelector(SELECTORS.frame);
      if (!video || !frame) return;

      video.pause();
      video.muted = true;
      this.#setMuteUI(cell, true);
      frame.classList.remove(CLASSES.paused);
      cell.querySelector(SELECTORS.videoWrapper)?.classList.remove(CLASSES.playing);
    }

    /** Stop every cell — used when the carousel scrolls out of view. */
    #pauseAll() {
      for (const cell of this.#cells) this.#pauseCell(cell);
    }

    /**
     * Start a cell's video at the given mute state.
     *
     * @param {HTMLElement} cell - the cell owning the video.
     * @param {HTMLVideoElement} video - the video to start.
     * @param {boolean} muted - whether to start it muted.
     * @returns {Promise<boolean>} whether playback actually started.
     */
    async #startPlayback(cell, video, muted) {
      video.muted = muted;
      this.#setMuteUI(cell, muted);

      try {
        await video.play();
        return true;
      } catch (error) {
        return false;
      }
    }

    /** Play the centered video; pause and re-mute every other one. */
    async #playSelected() {
      if (!this.#isVisible) return;

      const selected = this.#flkty.selectedIndex;

      // Pause every other cell up front, before awaiting playback, so two
      // videos are never running at once.
      for (const [index, cell] of this.#cells.entries()) {
        if (index !== selected) this.#pauseCell(cell);
      }

      const selectedCell = this.#cells[selected];
      if (!selectedCell) return;

      const video = this.#videoOf(selectedCell);
      const frame = selectedCell.querySelector(SELECTORS.frame);
      const wrapper = selectedCell.querySelector(SELECTORS.videoWrapper);
      if (!video || !frame) return;

      // Restore the visitor's sound choice, falling back to a muted start.
      const started =
        (this.#soundRequested && (await this.#startPlayback(selectedCell, video, false))) ||
        (await this.#startPlayback(selectedCell, video, true));

      if (!started) {
        // Autoplay was refused — show the frame as paused so the next tap
        // starts it with sound as a user gesture.
        frame.classList.add(CLASSES.paused);
        return;
      }

      // play() can take a while; this cell may no longer be the one to play.
      if (!this.#isVisible || this.#cells[this.#flkty.selectedIndex] !== selectedCell) {
        this.#pauseCell(selectedCell);
        return;
      }

      frame.classList.remove(CLASSES.paused);
      wrapper?.classList.add(CLASSES.playing);
    }

    /**
     * Toggle sound on a cell, or start it with sound when autoplay was
     * previously refused.
     *
     * @param {HTMLElement} cell - the cell that was tapped.
     */
    async #toggleMute(cell) {
      const video = this.#videoOf(cell);
      const frame = cell.querySelector(SELECTORS.frame);
      const wrapper = cell.querySelector(SELECTORS.videoWrapper);
      if (!video || !frame) return;

      // `video.paused`, not the class: the class only flips once play() resolves.
      if (!video.paused) {
        video.muted = !video.muted;
        this.#soundRequested = !video.muted;
        this.#setMuteUI(cell, video.muted);
        return;
      }

      // Nothing is playing: this tap is a user gesture, so start with sound.
      this.#soundRequested = true;

      if (await this.#startPlayback(cell, video, false)) {
        frame.classList.remove(CLASSES.paused);
        wrapper?.classList.add(CLASSES.playing);
      }
      // Still refused — leave the frame in its paused state.
    }
  }

  if (!customElements.get('social-videos-carousel')) {
    customElements.define('social-videos-carousel', SocialVideosCarousel);
  }
})();
